import { useTranslation } from 'react-i18next';

import { ExportButton } from '@/components/atoms/ExportButton/ExportButton';
import SearchBar from '@/components/atoms/SearchBar/SearchBar';
import { ComplexFilterBar } from '@/components/ui/custom/ComplexFilter/ComplexFilterBar';
import { ComplexFilterCombobox } from '@/components/ui/custom/ComplexFilter/ComplexFilterCombobox';
import { ColumnVisibilityMenu } from '@/components/ui/table/column-visibility-menu';

import { UseCampaignLogsPageReturn } from '../hooks/useCampaignLogsPage';
import { useExportCampaignLogs } from '../hooks/useExportCampaignLogs';

interface CampaignLogsToolbarProps {
  page: UseCampaignLogsPageReturn;
}

export const CampaignLogsToolbar = ({ page }: CampaignLogsToolbarProps) => {
  const { t } = useTranslation();
  const { exportLogs, isExporting } = useExportCampaignLogs({
    campaignId: page.campaignId,
    campaignName: page.campaign?.name,
    params: page.subscriberParams,
    campaignSenders: page.campaign_senders,
  });

  return (
    <>
      <div className="mb-4 flex flex-col gap-4 pl-1 sm:flex-row sm:items-center sm:justify-between">
        <SearchBar
          placeholder={t('campaigns.search_logs')}
          value={page.searchQuery}
          onChange={(e) => page.handleSearchChange(e.target.value)}
        />

        <div className="flex items-center gap-2">
          <ExportButton
            variant="icon"
            onExport={exportLogs}
            isExporting={isExporting}
            disabled={page.listStatus === 'pending' || page.totalRows === 0}
          />

          <ComplexFilterCombobox
            filterDefinitions={page.filterDefinitions}
            filters={page.filters}
            setFilter={page.setFilter}
            removeFilter={page.removeFilter}
            activeFilterCount={page.activeFilterCount}
            savedFilters={page.savedFilterItems}
            onApplySavedFilter={(item) => {
              const view = page.savedViews.find((v) => v.id === item.id);
              if (view) {
                page.applyView(view);
              }
            }}
            onOpenActivePill={page.setOpenPillId}
          />

          <ColumnVisibilityMenu
            columns={page.hideableColumns}
            columnVisibility={page.columnVisibility}
            onColumnVisibilityChange={page.setColumnVisibility}
            onReset={page.resetColumnVisibility}
            open={page.isCustomizeColumnsOpen}
            onOpenChange={page.setIsCustomizeColumnsOpen}
            onInteractOutside={(e) => {
              if (
                page.customizeButtonRef?.current &&
                e.target === page.customizeButtonRef.current
              ) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          />
        </div>
      </div>

      <ComplexFilterBar
        className="mb-4"
        filters={page.filterBadges}
        savedFiltersConfig={page.savedFiltersConfig}
        openPillId={page.openPillId}
        onOpenPillChange={page.setOpenPillId}
        onClearAll={() => {
          page.resetFilters();
          page.clearActiveView();
        }}
      />
    </>
  );
};
